// `AgentProse` — the sanitized markdown renderer for Beekeeper turns (tasks.md 3.1; design **T5**;
// `client-beekeeper-chat` "Agent prose renders as plain text", MODIFIED to sanitized markdown;
// sketch `prototype/sketches/sandbox-screen-consolidation/chat-modal.html`, visual spec §7.4).
//
// This module REVERSES the plain-text rule UI.md §4 / BEEKEEPER §7 shipped with (D7). Model output
// is attacker-influenced — a player's own prompt and their programs sit in the model's context, so
// a hostile player is the cheapest way to get a chosen string out the other side — and turning that
// string from text into markup is therefore a security decision that has to be paid for. The
// payment is the *shape of this file*, not a sanitizer bolted onto its output.
//
// The whole argument, in three properties:
//
//  1. **A closed tag set.** Every element is created through `emit`, whose tag parameter is the
//     `ProseTag` union. There is no other call into React's element factory in this module, so an
//     anchor, an image, an iframe or a script is not something this code declines to produce — it
//     is something it cannot express. A future edit that wants one has to widen a published type
//     that the test suite pins, which is a visible act rather than a quiet one.
//  2. **A closed prop set.** `emit` passes exactly `key`, `style` and an optional `data-prose`
//     mark, and `style` is always one of the frozen constants below — never anything derived from
//     the input. No prop name is ever computed, so no `on*` handler and no `href`/`src` can appear
//     however the input is shaped. React's raw-markup escape hatch is not referenced anywhere here
//     (`agent-prose.test.tsx` scans this file's source to keep it that way).
//  3. **No grammar for the dangerous constructs.** There is deliberately no HTML rule and no link
//     rule. Raw HTML is not escaped after parsing — it is never parsed at all, so `<script>…` is
//     just text that happens to look like a tag, shown in the `escaped` chip treatment so a player
//     can see the model tried. Markdown link and image syntax is likewise not in the grammar, so
//     `[label](url)` renders as its own literal source with the destination visible; a URL is
//     matched *only* so it can be drawn as an inert dotted `span`, which is a rendering rule with
//     no navigable output rather than a link with its teeth pulled. A filter has bypasses; an
//     absent code path does not, and only the second claim is provable over the whole adversarial
//     corpus in `__fixtures__/agent-prose-corpus.ts`.
//
// Robustness is the other half: a chat overlay that dies on an unterminated fence takes a running
// turn down with it, so every parser here is total. Unmatched delimiters degrade to literal text,
// unterminated fences and blockquotes run to end-of-input, and block nesting is depth-capped —
// there is no input for which this module throws.
//
// The renderer returns a Fragment rather than a wrapper element on purpose: the caller owns the
// `.prose` column's layout (`flex: 1; min-width: 0` in the chat row), and a wrapper would have to
// be a `span` — the only container in the permitted set — which cannot legally hold a `p`. Block
// typography therefore travels on the blocks themselves.

import { Fragment, createElement } from 'react';
import type { CSSProperties, ReactElement, ReactNode } from 'react';

/**
 * The complete set of element types this module can produce. Exported because it is the security
 * claim itself, and a test that could not read it would be asserting against a copy.
 */
export const PROSE_TAGS = [
  'p',
  'strong',
  'em',
  'code',
  'pre',
  'ul',
  'ol',
  'li',
  'h4',
  'blockquote',
  'span',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
] as const;

export type ProseTag = (typeof PROSE_TAGS)[number];

/**
 * The two deliberately *visible* treatments of the security envelope (visual spec §7.4). They are
 * marked in the DOM so the treatment is assertable and so a reviewer reading a live transcript can
 * tell "this URL is inert" from "this looked like a link and was not one" without guessing.
 */
type ProseMark = 'inert' | 'escaped';

interface ProseElementProps {
  readonly key: string;
  readonly style?: CSSProperties;
  readonly 'data-prose'?: ProseMark;
}

/**
 * The single element factory. Every element in the output passes through here, which is what makes
 * both closed sets — tags and props — true by construction rather than by convention.
 */
function emit(
  tag: ProseTag,
  key: string,
  style: CSSProperties,
  children: ReactNode,
  mark?: ProseMark,
): ReactElement {
  const props: ProseElementProps = { key, style, 'data-prose': mark };
  return createElement(tag, props, children);
}

// --- Styles (visual spec §7.4) ------------------------------------------------------------------
// Frozen constants, never derived from input — see property 2 in the header.

const BODY: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 'var(--text-sm)',
  lineHeight: 'var(--leading-body)',
  color: 'var(--ink-1)',
  // A model can emit an unbroken token longer than the 309 px prose column (corpus case), which
  // without this widens the chat row's min-content and pushes the whole transcript sideways.
  overflowWrap: 'anywhere',
};

const BLOCK_GAP = '0 0 7px';

const PARAGRAPH_STYLE: CSSProperties = { ...BODY, margin: BLOCK_GAP };
const PARAGRAPH_LAST_STYLE: CSSProperties = { ...BODY, margin: 0 };
const HEADING_STYLE: CSSProperties = {
  margin: '8px 0 5px',
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  fontSize: 14,
  color: 'var(--ink-1)',
  overflowWrap: 'anywhere',
};
const LIST_STYLE: CSSProperties = { ...BODY, margin: BLOCK_GAP, paddingLeft: 20 };
const LIST_ITEM_STYLE: CSSProperties = { margin: '2px 0' };
const QUOTE_STYLE: CSSProperties = {
  ...BODY,
  margin: BLOCK_GAP,
  padding: '4px 0 4px 10px',
  borderLeft: '3px solid var(--line-strong)',
  color: 'var(--ink-2)',
};
const PRE_STYLE: CSSProperties = {
  margin: BLOCK_GAP,
  background: 'var(--hud-bg-solid)',
  borderRadius: 'var(--r-sm)',
  padding: '8px 10px',
  overflowX: 'auto',
};
// On-dark code text: a raw hex, following `CodeView`'s precedent — the dark surface's foreground
// has no token because nothing else in the light kit uses it.
const PRE_CODE_STYLE: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  lineHeight: 1.6,
  color: '#E8DFC8',
};
const STRONG_STYLE: CSSProperties = { fontWeight: 800 };
const EM_STYLE: CSSProperties = { fontStyle: 'italic' };
const CODE_STYLE: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '11.5px',
  background: 'var(--paper-1)',
  border: '1px solid var(--line)',
  borderRadius: 5,
  padding: '1px 4px',
};
/** A URL: dotted-underlined so it reads as a destination, with nothing to click. */
const INERT_STYLE: CSSProperties = {
  color: 'var(--ink-2)',
  textDecoration: 'none',
  borderBottom: '1px dotted var(--line-strong)',
};
/** Markup the model emitted, shown as the literal characters it is. */
const ESCAPED_STYLE: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '11.5px',
  background: 'var(--paper-2)',
  borderRadius: 5,
  padding: '1px 4px',
  color: 'var(--ink-2)',
};
/** `display: block` + `overflow-x: auto` on the `table` element itself is the classic
 *  wrapper-free responsive-table trick: there is no container tag in the permitted set to hang a
 *  scroll region on (the closed tag set gains exactly the six table elements the task calls for,
 *  nothing else), so the table scrolls itself rather than being wrapped. */
// `border-collapse` is deliberately NOT set here: it only has meaning for a table-ish `display`,
// so on the `display: block` above it would be an inert property that reads as if borders were
// collapsing when they are not — and with a full `border` on every cell the shared edges would
// then render doubled. The cells below draw two sides each (right + bottom) and this element
// closes the remaining two, so every rule is 1px whatever the display mode.
const TABLE_STYLE: CSSProperties = {
  ...BODY,
  display: 'block',
  overflowX: 'auto',
  maxWidth: '100%',
  margin: BLOCK_GAP,
  borderTop: '1px solid var(--line)',
  borderLeft: '1px solid var(--line)',
};
// **No `overflow-wrap: anywhere` on either cell style.** It reads like the same long-token
// defence the rest of the prose column uses, but inside `TABLE_STYLE`'s `display: block` it
// drives every cell's min-content width down to about one character, so the table always
// shrink-to-fits the prose column and `overflow-x: auto` never engages: a wide table silently
// became a compressed, three-lines-per-cell one instead of a scrolling one. The scroll container
// *is* the long-token defence here, so nothing is lost by leaving it off.
const TABLE_HEAD_CELL_STYLE: CSSProperties = {
  borderRight: '1px solid var(--line)',
  borderBottom: '1px solid var(--line)',
  padding: '4px 8px',
  fontWeight: 700,
  background: 'var(--paper-2)',
  color: 'var(--ink-1)',
  textAlign: 'left',
};
const TABLE_BODY_CELL_STYLE: CSSProperties = {
  borderRight: '1px solid var(--line)',
  borderBottom: '1px solid var(--line)',
  padding: '4px 8px',
  color: 'var(--ink-1)',
};
/** `tr`/`thead`/`tbody` carry no styling of their own — borders and padding live on `th`/`td` — but
 *  `emit` still requires a `style` argument, so this is that frozen empty constant. */
const NO_STYLE: CSSProperties = {};

/**
 * Column alignment, read off the delimiter row's colons. This is the one place `style` is derived
 * from input rather than a frozen constant — but the derivation is a 3-way switch onto a closed
 * enum (`Alignment`), never an arbitrary string, so the "never anything derived from the input"
 * claim in the header still holds in spirit: nothing attacker-shaped reaches a CSS property name
 * or value here, only a selection among `left` | `center` | `right` | unset.
 */
function tableCellStyle(base: CSSProperties, align: Alignment): CSSProperties {
  if (align === 'none') return base;
  return { ...base, textAlign: align };
}

// --- The document model -------------------------------------------------------------------------

type Inline =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'code'; readonly text: string }
  | { readonly kind: 'inert'; readonly text: string }
  | { readonly kind: 'escaped'; readonly text: string }
  | { readonly kind: 'strong'; readonly children: readonly Inline[] }
  | { readonly kind: 'em'; readonly children: readonly Inline[] };

/** Column alignment read off the GFM delimiter row's colons. `none` means no colon was present. */
type Alignment = 'left' | 'center' | 'right' | 'none';

/** One table cell's parsed inline content. */
type TableCell = readonly Inline[];
/** One table row: a fixed-width tuple of cells, already normalized to the header's column count. */
type TableRow = readonly TableCell[];

type Block =
  | { readonly kind: 'paragraph'; readonly content: readonly Inline[] }
  | { readonly kind: 'heading'; readonly content: readonly Inline[] }
  | { readonly kind: 'fence'; readonly text: string }
  | { readonly kind: 'list'; readonly ordered: boolean; readonly items: readonly (readonly Block[])[] }
  | { readonly kind: 'quote'; readonly children: readonly Block[] }
  | {
      readonly kind: 'table';
      readonly header: TableRow;
      readonly align: readonly Alignment[];
      readonly rows: readonly TableRow[];
    };

/** Block nesting beyond this collapses to paragraphs. Deep nesting is a malformed-input shape, not
 *  a construct worth honouring, and an unbounded recursive descent is a denial of service. */
const MAX_BLOCK_DEPTH = 24;
/** The same bound for emphasis nesting; past it, delimiters are literal characters. */
const MAX_INLINE_DEPTH = 8;
/** A table wider than this keeps only its first N columns; the rest are dropped, deterministically,
 *  rather than growing the row unboundedly wide. */
const MAX_TABLE_COLUMNS = 12;
/** A table taller than this keeps only its first N body rows. */
const MAX_TABLE_ROWS = 200;
/** The **whole message's** table-cell budget — one full-size table's worth, spent across every
 *  table in the turn.
 *
 *  The per-table caps above bound one table and nothing else, so on their own a turn could still
 *  force unbounded work simply by repeating max-density tables. That is not theoretical: a cell
 *  carries an inline `style`, so at ~5.8 elements per input byte a 35 KB turn (well inside one
 *  model reply) renders ~209k elements, and `ChatOverlay` re-renders the whole message on every
 *  streaming delta. A document-wide budget is what actually makes the claim "a hostile turn
 *  cannot force an unbounded number of row elements onto the page" true.
 *
 *  Rows past the budget are not dropped silently: exactly as with the row cap, they end the table
 *  and fall through to the caller as ordinary paragraph text. */
const MAX_TABLE_CELLS_PER_MESSAGE = MAX_TABLE_ROWS * MAX_TABLE_COLUMNS;

/** The remaining message-wide cell allowance, threaded through the block parse. Mutable by
 *  design and deliberately *not* module state: one budget belongs to one `AgentProse` call. */
interface TableBudget {
  cells: number;
}

// --- Inline scanning ----------------------------------------------------------------------------

/** Characters a backslash may escape, matching CommonMark's ASCII punctuation set closely enough. */
const ESCAPABLE = /[\\`*_{}[\]()#+\-.!<>&|~^$%@'":;,?/=]/;

/** `<scheme:…>` — the autolink form, matched only to be inertised. */
const AUTOLINK_RE = /<([A-Za-z][A-Za-z0-9+.-]*:[^\s<>]*)>/y;
/** Anything tag-shaped. Matched only so it can be shown as literal characters; never interpreted.
 *  The negated class spans newlines deliberately — a payload broken across lines is still text. */
const TAGISH_RE = /<\/?[A-Za-z][^>]*>/y;
/** A URL with an explicit authority. Trailing sentence punctuation is trimmed by the caller. */
const URL_RE = /[A-Za-z][A-Za-z0-9+.-]*:\/\/[^\s<>()[\]]+/y;

function isWordChar(ch: string | undefined): boolean {
  return ch !== undefined && /[A-Za-z0-9]/.test(ch);
}

/** A backtick run at `at`, closed by a run of the same length. Content is never re-scanned. */
function matchCodeSpan(src: string, at: number): { text: string; next: number } | null {
  let openLen = 0;
  while (src[at + openLen] === '`') openLen += 1;
  const contentStart = at + openLen;
  let i = contentStart;
  while (i < src.length) {
    if (src[i] !== '`') {
      i += 1;
      continue;
    }
    let runLen = 0;
    while (src[i + runLen] === '`') runLen += 1;
    if (runLen === openLen) return { text: src.slice(contentStart, i), next: i + runLen };
    i += runLen;
  }
  return null;
}

/** Everything that can begin with `<`. None of the outcomes is markup. */
function matchAngle(src: string, at: number): { node: Inline; next: number } | null {
  if (src.startsWith('<!--', at)) {
    const end = src.indexOf('-->', at + 4);
    const stop = end === -1 ? src.length : end + 3;
    return { node: { kind: 'escaped', text: src.slice(at, stop) }, next: stop };
  }
  if (src.startsWith('<![CDATA[', at)) {
    const end = src.indexOf(']]>', at + 9);
    const stop = end === -1 ? src.length : end + 3;
    return { node: { kind: 'escaped', text: src.slice(at, stop) }, next: stop };
  }
  AUTOLINK_RE.lastIndex = at;
  const auto = AUTOLINK_RE.exec(src);
  if (auto !== null) {
    return { node: { kind: 'inert', text: auto[1] ?? '' }, next: at + auto[0].length };
  }
  TAGISH_RE.lastIndex = at;
  const tag = TAGISH_RE.exec(src);
  if (tag !== null) {
    return { node: { kind: 'escaped', text: tag[0] }, next: at + tag[0].length };
  }
  return null;
}

/** A bare URL, anchored at a word boundary so the scan stays linear over pathological single
 *  tokens (the alternative — attempting the pattern at every index — is quadratic). */
function matchUrl(src: string, at: number): { text: string; next: number } | null {
  if (isWordChar(src[at - 1])) return null;
  URL_RE.lastIndex = at;
  const m = URL_RE.exec(src);
  if (m === null) return null;
  let text = m[0];
  while (text.length > 0 && /[.,;:!?]/.test(text[text.length - 1] ?? '')) text = text.slice(0, -1);
  if (text.length === 0) return null;
  return { text, next: at + text.length };
}

/**
 * Per-`parseInline`-invocation memo of delimiters already proven to have **no closer anywhere in
 * the remaining string**, keyed by delimiter to the earliest content start that failed.
 *
 * Without it the scan is quadratic, and reachable from attacker-influenced agent prose. A failed
 * opener searches to end-of-string, then `parseInline` retries at the very next character, so
 * `*a *a *a …` re-scans the whole suffix per `*` — measured at 33 s for one table cell, inside
 * every advertised row/column/cell bound (implementation review,
 * `beekeeper-chat-markdown-tables`).
 *
 * The memo is sound because `closes` depends only on `src`, the candidate index, and the
 * delimiter — never on where the opener was. So if no index at or after `contentStart` closes
 * `delim`, no *later* opener of the same delimiter can close either: its search range is a suffix
 * of one already proven empty. Scanning left to right, the first failure records the smallest
 * failing start, which is why the entry is written once and never widened.
 */
type NoCloserMemo = Map<string, number>;

/** `**strong**`, `*em*`, `_em_`. An opener with no closer is left as a literal character. */
function matchEmphasis(
  src: string,
  at: number,
  depth: number,
  noCloser: NoCloserMemo,
): { node: Inline; next: number } | null {
  const ch = src[at];
  if (ch === undefined) return null;
  // `_` only opens at a word boundary, so `snake_case_identifiers` in prose stay intact.
  if (ch === '_' && isWordChar(src[at - 1])) return null;
  const strong = src[at + 1] === ch;
  const delim = strong ? ch + ch : ch;
  const contentStart = at + delim.length;
  const first = src[contentStart];
  if (first === undefined || /\s/.test(first)) return null;

  // Already proven closerless from an earlier — therefore wider — starting point.
  const failedFrom = noCloser.get(delim);
  if (failedFrom !== undefined && contentStart >= failedFrom) return null;

  let i = contentStart;
  while (i < src.length) {
    if (!src.startsWith(delim, i)) {
      i += 1;
      continue;
    }
    const before = src[i - 1];
    const after = src[i + delim.length];
    const closes = before !== undefined && !/\s/.test(before) && !(ch === '_' && isWordChar(after));
    if (closes) {
      const children = parseInline(src.slice(contentStart, i), depth + 1);
      return { node: { kind: strong ? 'strong' : 'em', children }, next: i + delim.length };
    }
    i += 1;
  }
  // The scan reached the end without closing: record it so no later opener of this delimiter
  // repeats the walk. Written only if absent — the earliest failure has the smallest start.
  if (failedFrom === undefined) noCloser.set(delim, contentStart);
  return null;
}

function parseInline(src: string, depth: number): readonly Inline[] {
  const out: Inline[] = [];
  let buffer = '';
  let i = 0;
  // Scoped to this `src`: a recursive call parses a different (shorter) string, so a failure
  // proven here says nothing there.
  const noCloser: NoCloserMemo = new Map();

  const flush = (): void => {
    if (buffer.length > 0) {
      out.push({ kind: 'text', text: buffer });
      buffer = '';
    }
  };

  while (i < src.length) {
    const ch = src[i] as string;

    if (ch === '\\') {
      const next = src[i + 1];
      if (next !== undefined && ESCAPABLE.test(next)) {
        buffer += next;
        i += 2;
        continue;
      }
    }

    if (ch === '`') {
      const span = matchCodeSpan(src, i);
      if (span !== null) {
        flush();
        out.push({ kind: 'code', text: span.text });
        i = span.next;
        continue;
      }
    }

    if (ch === '<') {
      const angle = matchAngle(src, i);
      if (angle !== null) {
        flush();
        out.push(angle.node);
        i = angle.next;
        continue;
      }
    }

    if ((ch === '*' || ch === '_') && depth < MAX_INLINE_DEPTH) {
      const emphasis = matchEmphasis(src, i, depth, noCloser);
      if (emphasis !== null) {
        flush();
        out.push(emphasis.node);
        i = emphasis.next;
        continue;
      }
    }

    if (/[A-Za-z]/.test(ch)) {
      const url = matchUrl(src, i);
      if (url !== null) {
        flush();
        out.push({ kind: 'inert', text: url.text });
        i = url.next;
        continue;
      }
    }

    buffer += ch;
    i += 1;
  }

  flush();
  return out;
}

// --- Block scanning -----------------------------------------------------------------------------

const HEADING_RE = /^ {0,3}(#{1,6})(?:[ \t]+(.*?))?[ \t]*$/;
const QUOTE_RE = /^ {0,3}>[ \t]?(.*)$/;
const FENCE_RE = /^ {0,3}(`{3,}|~{3,})(.*)$/;
const ITEM_RE = /^([ \t]*)([-*+]|\d{1,9}[.)])([ \t]*)(.*)$/;

interface FenceOpen {
  readonly marker: string;
  readonly length: number;
}

function fenceOpen(line: string): FenceOpen | null {
  const m = FENCE_RE.exec(line);
  if (m === null) return null;
  const run = m[1] as string;
  const info = m[2] as string;
  // A backtick fence's info string may not itself contain a backtick, which is what keeps a closing
  // fence from being mistaken for an opening one.
  if (run[0] === '`' && info.includes('`')) return null;
  return { marker: run[0] as string, length: run.length };
}

function isFenceClose(line: string, open: FenceOpen): boolean {
  const trimmed = line.trim();
  if (trimmed.length < open.length) return false;
  for (const ch of trimmed) if (ch !== open.marker) return false;
  return true;
}

interface ItemMatch {
  readonly indent: number;
  readonly ordered: boolean;
  readonly markerWidth: number;
  readonly text: string;
}

function matchItem(line: string): ItemMatch | null {
  const m = ITEM_RE.exec(line);
  if (m === null) return null;
  const lead = m[1] as string;
  const marker = m[2] as string;
  const gap = m[3] as string;
  const text = m[4] as string;
  // Without a gap this is not a list item but something that merely starts with `*` or a number —
  // `**bold`, `*em*`, `1.5` — and treating it as one would eat emphasis at the start of a line.
  if (gap.length === 0 && text.length > 0) return null;
  return {
    indent: lead.length,
    ordered: /\d/.test(marker[0] as string),
    markerWidth: marker.length + gap.length,
    text,
  };
}

function leadingWhitespace(line: string): number {
  let n = 0;
  while (line[n] === ' ' || line[n] === '\t') n += 1;
  return n;
}

function dedent(line: string, columns: number): string {
  let n = 0;
  while (n < columns && (line[n] === ' ' || line[n] === '\t')) n += 1;
  return line.slice(n);
}

/** A delimiter cell: hyphens with an optional leading and/or trailing colon for alignment. */
const TABLE_DELIM_CELL_RE = /^:?-+:?$/;

/** Whether the final `|` of `line` is escaped — i.e. preceded by an **odd** run of backslashes.
 *  `a\|` ends in an escaped pipe; `a\\|` ends in an escaped backslash followed by a real
 *  separator, and a plain `endsWith('\\|')` test cannot tell the two apart. */
function endsWithEscapedPipe(line: string): boolean {
  if (!line.endsWith('|')) return false;
  let backslashes = 0;
  let i = line.length - 2;
  while (line[i] === '\\') {
    backslashes += 1;
    i -= 1;
  }
  return backslashes % 2 === 1;
}

/**
 * Splits one pipe-table row into raw (untrimmed-of-markdown, whitespace-trimmed) cell strings.
 * Leading and trailing pipes are optional and stripped if present; a backslash-escaped pipe
 * (`\|`) is literal cell content, not a separator. Total cell count is not bounded here — the
 * caller normalizes to the header's (already column-capped) width — so this alone cannot be used
 * to force unbounded work.
 */
function splitTableRow(rawLine: string): string[] {
  let line = rawLine.trim();
  if (line.startsWith('|')) line = line.slice(1);
  if (line.endsWith('|') && !endsWithEscapedPipe(line)) line = line.slice(0, -1);

  const cells: string[] = [];
  let buffer = '';
  let i = 0;
  while (i < line.length) {
    const ch = line[i];
    if (ch === '\\' && line[i + 1] === '|') {
      buffer += '|';
      i += 2;
      continue;
    }
    if (ch === '|') {
      cells.push(buffer.trim());
      buffer = '';
      i += 1;
      continue;
    }
    buffer += ch;
    i += 1;
  }
  cells.push(buffer.trim());
  return cells;
}

/** A candidate delimiter row (`|---|:---:|---:|`), or `null` if the line is not shaped like one.
 *  Requires an actual pipe character so a bare underlined line (`Title` / `---`) — ordinary prose,
 *  and not a GFM table without a leading/trailing pipe anywhere — is never mistaken for a
 *  single-column table. */
function parseTableDelimiterRow(line: string): readonly Alignment[] | null {
  if (!line.includes('|')) return null;
  const cells = splitTableRow(line);
  if (cells.length === 0) return null;
  const align: Alignment[] = [];
  for (const cell of cells) {
    if (!TABLE_DELIM_CELL_RE.test(cell)) return null;
    const left = cell.startsWith(':');
    const right = cell.endsWith(':');
    align.push(left && right ? 'center' : right ? 'right' : left ? 'left' : 'none');
  }
  return align;
}

/** Pads or truncates a body row to the header's column count — GFM's rule for ragged rows,
 *  applied deterministically: a short row gets empty trailing cells, a long row's excess is
 *  dropped. */
function normalizeRowCells(cells: readonly string[], columns: number): string[] {
  const out = cells.slice(0, columns);
  while (out.length < columns) out.push('');
  return out;
}

interface TableHeaderMatch {
  readonly header: readonly string[];
  readonly align: readonly Alignment[];
}

/** Recognizes a table's header + delimiter pair at `lines[i]`/`lines[i + 1]`, or `null` if the
 *  shape does not hold — a header row's cell count must exactly match the delimiter row's, exactly
 *  as GFM requires, and a mismatch or a missing delimiter line means "this is not a table",
 *  leaving the line to fall through to ordinary paragraph text exactly as before this feature. */
function tableHeaderAt(lines: readonly string[], i: number): TableHeaderMatch | null {
  const headerLine = lines[i];
  if (headerLine === undefined || headerLine.trim().length === 0 || !headerLine.includes('|')) return null;
  const delimLine = lines[i + 1];
  if (delimLine === undefined) return null;
  const align = parseTableDelimiterRow(delimLine);
  if (align === null) return null;
  const header = splitTableRow(headerLine);
  if (header.length === 0 || header.length !== align.length) return null;
  return { header, align };
}

/** Parses a whole pipe table starting at `lines[start]`, given a table was already recognized
 *  there. Consumes the header, the delimiter, and every contiguous pipe-bearing line after it
 *  that does not begin another block; the first line that begins a block (blank, heading, quote,
 *  list item, fence or a new table), has no pipe, or would exceed the row cap or the message's
 *  cell budget ends the table and is left for the caller to parse as the next block — which, for
 *  an over-long run of table-shaped lines, is what "excess degrades to text" means here: the
 *  surplus rows are simply ordinary paragraph text.
 *
 *  The block-start test is what stops a table swallowing the construct that follows it when the
 *  agent left no blank line between them: `- item | pipe` after a table is a list, not two more
 *  cells. A genuine body row (`| 1 | 2 |`) begins no block, so ordinary tables are unaffected. */
function parseTable(
  lines: readonly string[],
  start: number,
  depth: number,
  budget: TableBudget,
): { block: Block; next: number } {
  const head = tableHeaderAt(lines, start) as TableHeaderMatch;
  const columns = Math.min(head.header.length, MAX_TABLE_COLUMNS);
  const align = head.align.slice(0, columns);
  const header: TableRow = head.header.slice(0, columns).map((cell) => parseInline(cell, 0));
  budget.cells -= columns;

  const rows: TableRow[] = [];
  let i = start + 2;
  while (i < lines.length && rows.length < MAX_TABLE_ROWS && budget.cells >= columns) {
    const line = lines[i] as string;
    if (startsBlock(lines, i, depth) || !line.includes('|')) break;
    const cells = normalizeRowCells(splitTableRow(line), columns);
    rows.push(cells.map((cell) => parseInline(cell, 0)));
    budget.cells -= columns;
    i += 1;
  }

  return { block: { kind: 'table', header, align, rows }, next: i };
}

function startsBlock(lines: readonly string[], i: number, depth: number): boolean {
  const line = lines[i] as string;
  if (line.trim().length === 0) return true;
  if (fenceOpen(line) !== null) return true;
  if (HEADING_RE.test(line)) return true;
  if (depth < MAX_BLOCK_DEPTH && QUOTE_RE.test(line)) return true;
  if (depth < MAX_BLOCK_DEPTH && matchItem(line) !== null) return true;
  if (depth < MAX_BLOCK_DEPTH && tableHeaderAt(lines, i) !== null) return true;
  return false;
}

function parseList(
  lines: readonly string[],
  start: number,
  depth: number,
  budget: TableBudget,
): { block: Block; next: number } {
  const first = matchItem(lines[start] as string) as ItemMatch;
  const { indent, ordered } = first;
  const items: (readonly Block[])[] = [];
  let i = start;

  while (i < lines.length) {
    const marker = matchItem(lines[i] as string);
    if (marker === null || marker.indent !== indent || marker.ordered !== ordered) break;
    const content: string[] = [marker.text];
    i += 1;

    while (i < lines.length) {
      const line = lines[i] as string;
      if (line.trim().length === 0) {
        // A blank line only stays inside the item if indented content follows it.
        let j = i;
        while (j < lines.length && (lines[j] as string).trim().length === 0) j += 1;
        if (j < lines.length && leadingWhitespace(lines[j] as string) > indent) {
          for (let k = i; k < j; k += 1) content.push('');
          i = j;
          continue;
        }
        break;
      }
      if (leadingWhitespace(line) > indent) {
        content.push(dedent(line, indent + marker.markerWidth));
        i += 1;
        continue;
      }
      break;
    }

    items.push(parseBlocks(content, depth + 1, budget));
  }

  return { block: { kind: 'list', ordered, items }, next: i };
}

function parseBlocks(lines: readonly string[], depth: number, budget: TableBudget): readonly Block[] {
  const out: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i] as string;

    if (line.trim().length === 0) {
      i += 1;
      continue;
    }

    const fence = fenceOpen(line);
    if (fence !== null) {
      const body: string[] = [];
      i += 1;
      while (i < lines.length && !isFenceClose(lines[i] as string, fence)) {
        body.push(lines[i] as string);
        i += 1;
      }
      // An unterminated fence simply runs to the end of the input; `i` may already be past the end.
      if (i < lines.length) i += 1;
      out.push({ kind: 'fence', text: body.join('\n') });
      continue;
    }

    const heading = HEADING_RE.exec(line);
    if (heading !== null) {
      out.push({ kind: 'heading', content: parseInline(heading[2] ?? '', 0) });
      i += 1;
      continue;
    }

    if (depth < MAX_BLOCK_DEPTH && QUOTE_RE.test(line)) {
      const inner: string[] = [];
      while (i < lines.length) {
        const quoted = QUOTE_RE.exec(lines[i] as string);
        if (quoted === null) break;
        inner.push(quoted[1] ?? '');
        i += 1;
      }
      out.push({ kind: 'quote', children: parseBlocks(inner, depth + 1, budget) });
      continue;
    }

    if (depth < MAX_BLOCK_DEPTH && matchItem(line) !== null) {
      const list = parseList(lines, i, depth, budget);
      out.push(list.block);
      i = list.next;
      continue;
    }

    // A spent budget stops admitting tables at all, rather than letting a run of bare
    // header+delimiter pairs keep minting twelve `th` each — the surplus is paragraph text.
    if (depth < MAX_BLOCK_DEPTH && budget.cells > 0 && tableHeaderAt(lines, i) !== null) {
      const table = parseTable(lines, i, depth, budget);
      out.push(table.block);
      i = table.next;
      continue;
    }

    // A paragraph always consumes its first line before testing the rest, so the loop cannot stall
    // on a line that reports as a block start but was refused above (past the depth cap).
    const paragraph: string[] = [line];
    i += 1;
    while (i < lines.length && !startsBlock(lines, i, depth)) {
      paragraph.push(lines[i] as string);
      i += 1;
    }
    out.push({ kind: 'paragraph', content: parseInline(paragraph.join('\n'), 0) });
  }

  return out;
}

/** CRLF, lone CR and LF all terminate a line — a truncated stream mixes them (corpus case). */
function splitLines(text: string): readonly string[] {
  return text.split(/\r\n|\r|\n/);
}

// --- Rendering ----------------------------------------------------------------------------------

function renderInline(nodes: readonly Inline[], keyPrefix: string): ReactNode[] {
  return nodes.map((node, index) => {
    const key = `${keyPrefix}i${index}`;
    switch (node.kind) {
      // A bare string child: React escapes it, so entity sequences and angle brackets stay the
      // characters the model wrote rather than becoming the markup they spell.
      case 'text':
        return node.text;
      case 'code':
        return emit('code', key, CODE_STYLE, node.text);
      case 'inert':
        return emit('span', key, INERT_STYLE, node.text, 'inert');
      case 'escaped':
        return emit('span', key, ESCAPED_STYLE, node.text, 'escaped');
      case 'strong':
        return emit('strong', key, STRONG_STYLE, renderInline(node.children, `${key}-`));
      case 'em':
        return emit('em', key, EM_STYLE, renderInline(node.children, `${key}-`));
    }
  });
}

function renderBlock(block: Block, key: string, last: boolean): ReactElement {
  switch (block.kind) {
    case 'paragraph':
      return emit(
        'p',
        key,
        last ? PARAGRAPH_LAST_STYLE : PARAGRAPH_STYLE,
        renderInline(block.content, `${key}-`),
      );
    case 'heading':
      return emit('h4', key, HEADING_STYLE, renderInline(block.content, `${key}-`));
    case 'fence':
      return emit('pre', key, PRE_STYLE, emit('code', `${key}-c`, PRE_CODE_STYLE, block.text));
    case 'quote':
      return emit('blockquote', key, QUOTE_STYLE, renderBlocks(block.children, `${key}-`));
    case 'list':
      return emit(
        block.ordered ? 'ol' : 'ul',
        key,
        LIST_STYLE,
        block.items.map((item, index) => {
          const itemKey = `${key}-l${index}`;
          // Tight items: a lone paragraph renders its inlines straight into the `li`, so a one-line
          // bullet does not acquire a paragraph's block margins.
          const only = item.length === 1 ? item[0] : undefined;
          const children =
            only !== undefined && only.kind === 'paragraph'
              ? renderInline(only.content, `${itemKey}-`)
              : renderBlocks(item, `${itemKey}-`);
          return emit('li', itemKey, LIST_ITEM_STYLE, children);
        }),
      );
    case 'table': {
      const headRow = emit(
        'tr',
        `${key}-hr`,
        NO_STYLE,
        block.header.map((cell, ci) =>
          emit(
            'th',
            `${key}-h${ci}`,
            tableCellStyle(TABLE_HEAD_CELL_STYLE, block.align[ci] ?? 'none'),
            renderInline(cell, `${key}-h${ci}-`),
          ),
        ),
      );
      const bodyRows = block.rows.map((row, ri) =>
        emit(
          'tr',
          `${key}-r${ri}`,
          NO_STYLE,
          row.map((cell, ci) =>
            emit(
              'td',
              `${key}-r${ri}c${ci}`,
              tableCellStyle(TABLE_BODY_CELL_STYLE, block.align[ci] ?? 'none'),
              renderInline(cell, `${key}-r${ri}c${ci}-`),
            ),
          ),
        ),
      );
      return emit('table', key, TABLE_STYLE, [
        emit('thead', `${key}-thead`, NO_STYLE, headRow),
        emit('tbody', `${key}-tbody`, NO_STYLE, bodyRows),
      ]);
    }
  }
}

function renderBlocks(blocks: readonly Block[], keyPrefix: string): ReactElement[] {
  return blocks.map((block, index) =>
    renderBlock(block, `${keyPrefix}b${index}`, index === blocks.length - 1),
  );
}

export interface AgentProseProps {
  /** The agent's turn text, verbatim off the wire. Untrusted by assumption. */
  readonly text: string;
}

/**
 * Renders one Beekeeper message. Returns a Fragment — the caller owns the prose column's layout
 * (see the header note on why there is no wrapper element).
 */
export function AgentProse({ text }: AgentProseProps): ReactElement {
  const blocks = parseBlocks(splitLines(text), 0, { cells: MAX_TABLE_CELLS_PER_MESSAGE });
  return createElement(Fragment, null, renderBlocks(blocks, ''));
}
